import * as React from 'react';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import BasicHeaderWithLogo from 'components/BasicHeaderWithLogo';
import HeaderNotifications from 'components/header/header-notifications/HeaderNotifications';
import Navigation from 'components/navigation/Navigation';
import UnreadNotificationsChecker from 'components/UnreadNotificationsChecker';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import UserDropdown from 'modules/user/components/dropdown/user-dropdown/UserDropdown';
import HelpMenu from 'modules/help-menu/HelpMenu';

import { isDisableUnreadMessagesSelector } from 'selectors/disableUnreadMessages';

import { getBrandIdsWithPermissionToAddBrandsToCrawler } from 'utils/access';
import {
  getEntityFromOrganization,
  getEntityFromProfile,
  getEntityFromUser
} from 'utils/view';
import { getAdditionalNavigationForOrganization, getNavigationForOrganization } from 'utils/navigation';
import { isInfluencersFocusedOrganization } from 'utils/organization';

import './header.scss';

import type { Account, BrandProfile, Entity, NavigationNavItem, Organization } from 'types';

type StateProps = {
  currentAccount: Account,
  currentOrganization: Organization,
  leftNavigationItems: Array<NavigationNavItem>,
  rightNavigationItems: Array<NavigationNavItem>,
  isInfluencer: boolean,
  disableUnreadMessages: boolean
};

type Props = StateProps;

class Header extends React.PureComponent<Props> {
  getProfileEntity(profile: BrandProfile): Entity | null {
    return getEntityFromProfile(profile);
  }

  getOrganizationEntity(): Entity | null {
    const { currentOrganization } = this.props;

    return getEntityFromOrganization(currentOrganization);
  }

  getUserEntity(): Entity {
    const { currentAccount } = this.props;

    return getEntityFromUser(currentAccount);
  }

  renderAccount(): React.ReactNode {
    const { currentOrganization } = this.props;

    let entity;

    if (isNil(currentOrganization)) {
      entity = this.getUserEntity();
    } else {
      const { influencerProfiles, brandProfiles } = currentOrganization;

      if (!isNil(influencerProfiles) && influencerProfiles.length > 0) {
        entity = this.getProfileEntity(influencerProfiles[0]);
      } else if (!isNil(brandProfiles) && brandProfiles.length > 0) {
        entity = this.getProfileEntity(brandProfiles[0]);
      } else {
        entity = this.getOrganizationEntity();
      }
    }

    return (
      <div className="header__profile">
        <UserDropdown>
          <div className="header__profile-name text-overflow-ellipsis" data-hj-suppress>
            {entity.displayName}
          </div>
          <div className="header__profile-icon">
            <EntityAvatar
              bordered
              entity={entity}
              customSize={{ width: 24, height: 24 }}
            />
          </div>
          <div className="header__chevron">
            <SVGDynamicIcon
              name="chevron-down-custom-fg"
              width={15}
              height={8}
              customColor="white"
            />
          </div>
          <div className="header__profile-avatar">
            <EntityAvatar
              entity={entity}
              bordered
            />
          </div>
        </UserDropdown>
      </div>
    );
  }

  render(): React.ReactNode {
    const {
      currentAccount,
      currentOrganization,
      leftNavigationItems,
      rightNavigationItems,
      isInfluencer,
      disableUnreadMessages
    } = this.props;

    return (
      <BasicHeaderWithLogo>
        <div className="header__nav">
          <Navigation
            items={leftNavigationItems}
            currentOrganization={currentOrganization}
          />
        </div>
        <div className="header__content">
          {isInfluencer && (
            <Navigation
              items={rightNavigationItems}
              currentOrganization={currentOrganization}
            />
          )}
          {!isInfluencer && !currentOrganization?.isFreemium && (
            <div className="header__notifications header__notifications--left">
              <HelpMenu />
            </div>
          )}
          {
            !disableUnreadMessages && !currentOrganization?.isFreemium && (
              <div className="header__notifications header__notifications--right">
                <HeaderNotifications />
              </div>
            )
          }
          {
            currentAccount &&
              this.renderAccount()
          }
        </div>
        {
          !disableUnreadMessages && currentOrganization && (
            <UnreadNotificationsChecker
              key={`unread-notifications-${currentOrganization.id}`} // reset polling if organization switched
              organizationId={currentOrganization.id}
            />
          )
        }
      </BasicHeaderWithLogo>
    );
  }
}

const mapStateToProps = ({ organizations, account, runtimeConfig }): StateProps => {
  const currentOrganization = organizations.currentOrganization;
  const currentAccount = account.currentAccount;
  const roles = account.roles;
  const brandsIdsWithAccessToMisc = getBrandIdsWithPermissionToAddBrandsToCrawler();
  const leftNavigationItems = currentAccount
    ? getNavigationForOrganization(currentOrganization, currentAccount, brandsIdsWithAccessToMisc, roles)
    : [];
  const rightNavigationItems = currentAccount
    ? getAdditionalNavigationForOrganization(currentOrganization)
    : [];
  const isInfluencer = isInfluencersFocusedOrganization(currentOrganization);
  const disableUnreadMessages = isDisableUnreadMessagesSelector({ runtimeConfig, account });

  return {
    currentOrganization,
    currentAccount,
    leftNavigationItems,
    rightNavigationItems,
    isInfluencer,
    disableUnreadMessages
  };
};

export default connect(mapStateToProps)(Header);
